import { toISODateString } from '@/lib/date'

export interface Student {
  id: string
  number: number
  name: string
  avatarInitials: string
}

export interface AbsenceRecord {
  studentId: string
  date: string
  periods: number[]
  reason: string | null
  excused: boolean
}

export type AbsenceNoteStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'needs_clarification'
  | 'unresolved'

export type AbsenceNoteFlag =
  | 'late_submission'
  | 'frequent_absences'
  | 'repeated_excuses'
  | 'missing_info'
  | 'suspicious_pattern'
  | 'monday_friday'

export interface InternalComment {
  id: string
  author: string
  text: string
  createdAt: string
}

export interface AuditEvent {
  action: string
  by: string
  at: string
}

export interface AbsenceNote {
  id: string
  studentId: string
  submittedBy: { name: string; relationship: string }
  dateFrom: string
  dateTo: string
  periods: number[]
  isFullDay: boolean
  parentNote: string
  submittedAt: string
  isLateSubmission: boolean
  linkedAttendanceCount: number
  matchQuality: 'full' | 'partial' | 'none'
  status: AbsenceNoteStatus
  processedAt?: string
  processedBy?: string
  flags: AbsenceNoteFlag[]
  internalComments: InternalComment[]
  auditTrail: AuditEvent[]
}

export interface MissingAbsenceNote {
  id: string
  studentId: string
  absenceDates: string[]
  totalPeriods: number
  daysSinceAbsence: number
  reminderSent: boolean
  reminderSentAt?: string
}

export interface GradeAlert {
  studentId: string
  subject: string
  grade: string
  trend: 'down' | 'up'
  previousGrade: string
}

export type NoteCategory =
  | 'behavior'
  | 'wellbeing'
  | 'social'
  | 'academic'
  | 'parent'
  | 'positive'
  | 'followup'
export type NoteStatus = 'info' | 'needs_followup' | 'monitoring' | 'resolved'

export interface ColleagueNote {
  id: string
  authorName: string
  authorInitials: string
  studentId: string
  body: string
  createdAt: string
  isNew: boolean
  category: NoteCategory
  status: NoteStatus
  priority: 'normal' | 'high'
}

export interface AttendanceDay {
  studentId: string
  status: 'present' | 'absent' | 'late' | 'exempt'
  periods: number[]
  excused?: boolean
  minutesLate?: number
  exemptSubject?: string
  note?: string
}

export const TOTAL_PERIODS = 7

export type AlertTier = 'none' | 'warning' | 'concern' | 'critical'

export interface StudentAttendanceStats {
  studentId: string
  totalScheduledLessons: number
  unexcusedAbsences: number
  excusedAbsences: number
  latenessCount: number
  unexcusedPercent: number
  trend: 'improving' | 'stable' | 'worsening'
  frequentlyMissedPeriods: number[] | null
  alertTier: AlertTier
}

export interface WeekDaySummary {
  date: string
  status: 'present' | 'absent' | 'late' | 'exempt' | 'future'
  absentPeriods: number
  totalPeriods: number
  excused: boolean
  minutesLate?: number
}

export interface StudentWeekAttendance {
  studentId: string
  days: WeekDaySummary[]
  weekAbsentDays: number
  weekLateDays: number
}

export const STUDENTS: Student[] = [
  { id: 's1', number: 1, name: 'Anna Bērziņa', avatarInitials: 'AB' },
  { id: 's2', number: 2, name: 'Kārlis Ozoliņš', avatarInitials: 'KO' },
  { id: 's3', number: 3, name: 'Elīna Kalniņa', avatarInitials: 'EK' },
  { id: 's4', number: 4, name: 'Mārtiņš Jansons', avatarInitials: 'MJ' },
  { id: 's5', number: 5, name: 'Ieva Liepiņa', avatarInitials: 'IL' },
  { id: 's6', number: 6, name: 'Rihards Vanags', avatarInitials: 'RV' },
  { id: 's7', number: 7, name: 'Laura Zariņa', avatarInitials: 'LZ' },
  { id: 's8', number: 8, name: 'Emīls Krūmiņš', avatarInitials: 'EKr' },
  { id: 's9', number: 9, name: 'Alise Vītoliņa', avatarInitials: 'AV' },
  { id: 's10', number: 10, name: 'Roberts Feldmanis', avatarInitials: 'RF' },
  { id: 's11', number: 11, name: 'Madara Priede', avatarInitials: 'MP' },
  { id: 's12', number: 12, name: 'Daniels Celmiņš', avatarInitials: 'DC' },
  { id: 's13', number: 13, name: 'Sofija Siliņa', avatarInitials: 'SS' },
  { id: 's14', number: 14, name: 'Gustavs Eglītis', avatarInitials: 'GE' },
  { id: 's15', number: 15, name: 'Marta Rudzīte', avatarInitials: 'MR' },
  { id: 's16', number: 16, name: 'Edgars Balodis', avatarInitials: 'EB' },
  { id: 's17', number: 17, name: 'Katrīna Vētra', avatarInitials: 'KV' },
  { id: 's18', number: 18, name: 'Artūrs Rieksts', avatarInitials: 'AR' },
  { id: 's19', number: 19, name: 'Eva Sproģe', avatarInitials: 'ES' },
  { id: 's20', number: 20, name: 'Markuss Dzenis', avatarInitials: 'MD' },
  { id: 's21', number: 21, name: 'Liene Kaža', avatarInitials: 'LK' },
  { id: 's22', number: 22, name: 'Toms Vilciņš', avatarInitials: 'TV' },
  { id: 's23', number: 23, name: 'Kitija Bite', avatarInitials: 'KB' },
  { id: 's24', number: 24, name: 'Nils Gailis', avatarInitials: 'NG' },
  { id: 's25', number: 25, name: 'Lauma Skuja', avatarInitials: 'LS' },
  { id: 's26', number: 26, name: 'Krists Lācis', avatarInitials: 'KL' },
]

export interface TeacherClass {
  id: string
  name: string
  studentCount: number
}

export const TEACHER_CLASSES: TeacherClass[] = [
  { id: '9a', name: '9.a', studentCount: 26 },
  { id: '11b', name: '11.b', studentCount: 22 },
]

export const CLASS_NAME = '9.a'
export const CLASS_SIZE = STUDENTS.length

export const TODAY_ABSENCES: AbsenceRecord[] = [
  {
    studentId: 's2',
    date: '2026-03-27',
    periods: [1, 2, 3, 4, 5, 6, 7],
    reason: null,
    excused: false,
  },
  {
    studentId: 's5',
    date: '2026-03-27',
    periods: [1, 2, 3],
    reason: 'Doctor appointment',
    excused: true,
  },
  { studentId: 's9', date: '2026-03-27', periods: [4, 5], reason: null, excused: false },
  {
    studentId: 's14',
    date: '2026-03-27',
    periods: [1, 2, 3, 4, 5, 6, 7],
    reason: 'Sick',
    excused: true,
  },
]

export const ABSENCE_NOTES: AbsenceNote[] = [
  {
    id: 'an1',
    studentId: 's2',
    submittedBy: { name: 'Aivars Ozoliņš', relationship: 'Father' },
    dateFrom: '2026-03-26',
    dateTo: '2026-03-26',
    periods: [1, 2, 3, 4, 5, 6],
    isFullDay: true,
    parentNote: 'Was at the doctor, forgot to notify. Please excuse the absence.',
    submittedAt: '2026-03-27T08:15:00',
    isLateSubmission: true,
    linkedAttendanceCount: 6,
    matchQuality: 'full',
    status: 'pending',
    flags: ['late_submission', 'frequent_absences'],
    internalComments: [],
    auditTrail: [{ action: 'Note submitted', by: 'Aivars Ozoliņš', at: '2026-03-27T08:15:00' }],
  },
  {
    id: 'an2',
    studentId: 's9',
    submittedBy: { name: 'Līga Vītoliņa', relationship: 'Mother' },
    dateFrom: '2026-03-25',
    dateTo: '2026-03-25',
    periods: [5, 6, 7],
    isFullDay: false,
    parentNote: 'At the hospital for medical tests.',
    submittedAt: '2026-03-26T19:30:00',
    isLateSubmission: true,
    linkedAttendanceCount: 3,
    matchQuality: 'full',
    status: 'pending',
    flags: ['late_submission'],
    internalComments: [],
    auditTrail: [{ action: 'Note submitted', by: 'Līga Vītoliņa', at: '2026-03-26T19:30:00' }],
  },
  {
    id: 'an3',
    studentId: 's20',
    submittedBy: { name: 'Aigars Dzenis', relationship: 'Father' },
    dateFrom: '2026-03-24',
    dateTo: '2026-03-24',
    periods: [1, 2],
    isFullDay: false,
    parentNote: 'Dentist appointment.',
    submittedAt: '2026-03-24T10:00:00',
    isLateSubmission: false,
    linkedAttendanceCount: 2,
    matchQuality: 'full',
    status: 'pending',
    flags: [],
    internalComments: [],
    auditTrail: [{ action: 'Note submitted', by: 'Aigars Dzenis', at: '2026-03-24T10:00:00' }],
  },
  {
    id: 'an4',
    studentId: 's4',
    submittedBy: { name: 'Sandra Jansone', relationship: 'Mother' },
    dateFrom: '2026-03-27',
    dateTo: '2026-03-28',
    periods: [1, 2, 3, 4, 5, 6, 7],
    isFullDay: true,
    parentNote: 'Family reasons.',
    submittedAt: '2026-03-29T11:00:00',
    isLateSubmission: true,
    linkedAttendanceCount: 14,
    matchQuality: 'partial',
    status: 'needs_clarification',
    processedAt: '2026-03-29T14:00:00',
    processedBy: 'Class Teacher',
    flags: ['late_submission', 'repeated_excuses', 'missing_info'],
    internalComments: [
      {
        id: 'ic1',
        author: 'Class Teacher',
        text: 'Vague reason — requested more detail from parent.',
        createdAt: '2026-03-29T14:00:00',
      },
    ],
    auditTrail: [
      { action: 'Note submitted', by: 'Sandra Jansone', at: '2026-03-29T11:00:00' },
      { action: 'Clarification requested', by: 'Class Teacher', at: '2026-03-29T14:00:00' },
    ],
  },
  {
    id: 'an5',
    studentId: 's8',
    submittedBy: { name: 'Andris Krūmiņš', relationship: 'Father' },
    dateFrom: '2026-03-24',
    dateTo: '2026-03-24',
    periods: [1, 2, 3],
    isFullDay: false,
    parentNote: 'Overslept.',
    submittedAt: '2026-03-25T09:00:00',
    isLateSubmission: true,
    linkedAttendanceCount: 3,
    matchQuality: 'full',
    status: 'unresolved',
    processedAt: '2026-03-26T08:00:00',
    processedBy: 'Class Teacher',
    flags: ['late_submission', 'frequent_absences', 'monday_friday'],
    internalComments: [
      {
        id: 'ic2',
        author: 'Class Teacher',
        text: 'Third Monday absence this month. Need to monitor.',
        createdAt: '2026-03-26T08:00:00',
      },
    ],
    auditTrail: [
      { action: 'Note submitted', by: 'Andris Krūmiņš', at: '2026-03-25T09:00:00' },
      { action: 'Marked as unresolved', by: 'Class Teacher', at: '2026-03-26T08:00:00' },
    ],
  },
  {
    id: 'an6',
    studentId: 's14',
    submittedBy: { name: 'Guntis Eglītis', relationship: 'Father' },
    dateFrom: '2026-03-23',
    dateTo: '2026-03-23',
    periods: [1, 2, 3, 4, 5, 6, 7],
    isFullDay: true,
    parentNote: 'Gustavs was sick with fever — stayed home all day.',
    submittedAt: '2026-03-23T18:00:00',
    isLateSubmission: false,
    linkedAttendanceCount: 7,
    matchQuality: 'full',
    status: 'approved',
    processedAt: '2026-03-24T09:10:00',
    processedBy: 'Class Teacher',
    flags: [],
    internalComments: [],
    auditTrail: [
      { action: 'Note submitted', by: 'Guntis Eglītis', at: '2026-03-23T18:00:00' },
      { action: 'Approved', by: 'Class Teacher', at: '2026-03-24T09:10:00' },
    ],
  },
  {
    id: 'an7',
    studentId: 's4',
    submittedBy: { name: 'Sandra Jansone', relationship: 'Mother' },
    dateFrom: '2026-03-21',
    dateTo: '2026-03-21',
    periods: [5, 6],
    isFullDay: false,
    parentNote: 'Mārtiņš had a regional sports competition in the afternoon.',
    submittedAt: '2026-03-21T15:00:00',
    isLateSubmission: false,
    linkedAttendanceCount: 2,
    matchQuality: 'full',
    status: 'approved',
    processedAt: '2026-03-24T09:12:00',
    processedBy: 'Class Teacher',
    flags: [],
    internalComments: [],
    auditTrail: [
      { action: 'Note submitted', by: 'Sandra Jansone', at: '2026-03-21T15:00:00' },
      { action: 'Approved', by: 'Class Teacher', at: '2026-03-24T09:12:00' },
    ],
  },
  {
    id: 'an8',
    studentId: 's9',
    submittedBy: { name: 'Līga Vītoliņa', relationship: 'Mother' },
    dateFrom: '2026-03-20',
    dateTo: '2026-03-20',
    periods: [1, 2, 3],
    isFullDay: false,
    parentNote: 'Family event.',
    submittedAt: '2026-03-20T20:00:00',
    isLateSubmission: false,
    linkedAttendanceCount: 3,
    matchQuality: 'full',
    status: 'rejected',
    processedAt: '2026-03-23T08:30:00',
    processedBy: 'Class Teacher',
    flags: ['repeated_excuses'],
    internalComments: [
      {
        id: 'ic3',
        author: 'Class Teacher',
        text: 'Same vague "family event" used multiple times. Rejected.',
        createdAt: '2026-03-23T08:30:00',
      },
    ],
    auditTrail: [
      { action: 'Note submitted', by: 'Līga Vītoliņa', at: '2026-03-20T20:00:00' },
      { action: 'Rejected', by: 'Class Teacher', at: '2026-03-23T08:30:00' },
    ],
  },
  {
    id: 'an9',
    studentId: 's5',
    submittedBy: { name: 'Māra Liepiņa', relationship: 'Mother' },
    dateFrom: '2026-03-27',
    dateTo: '2026-03-27',
    periods: [1, 2, 3],
    isFullDay: false,
    parentNote: 'Doctor appointment — scheduled in advance, please excuse morning absence.',
    submittedAt: '2026-03-26T21:14:00',
    isLateSubmission: false,
    linkedAttendanceCount: 3,
    matchQuality: 'full',
    status: 'approved',
    processedAt: '2026-03-27T08:00:00',
    processedBy: 'Class Teacher',
    flags: [],
    internalComments: [],
    auditTrail: [
      { action: 'Note submitted', by: 'Māra Liepiņa', at: '2026-03-26T21:14:00' },
      { action: 'Approved', by: 'Class Teacher', at: '2026-03-27T08:00:00' },
    ],
  },
  {
    id: 'an10',
    studentId: 's12',
    submittedBy: { name: 'Māris Celmiņš', relationship: 'Father' },
    dateFrom: '2026-03-19',
    dateTo: '2026-03-19',
    periods: [6, 7],
    isFullDay: false,
    parentNote: 'Had to leave early.',
    submittedAt: '2026-03-22T10:30:00',
    isLateSubmission: true,
    linkedAttendanceCount: 2,
    matchQuality: 'partial',
    status: 'rejected',
    processedAt: '2026-03-24T10:00:00',
    processedBy: 'Class Teacher',
    flags: ['late_submission', 'missing_info'],
    internalComments: [
      {
        id: 'ic4',
        author: 'Class Teacher',
        text: 'No specific reason given, submitted 3 days late.',
        createdAt: '2026-03-24T10:00:00',
      },
    ],
    auditTrail: [
      { action: 'Note submitted', by: 'Māris Celmiņš', at: '2026-03-22T10:30:00' },
      { action: 'Rejected', by: 'Class Teacher', at: '2026-03-24T10:00:00' },
    ],
  },
  {
    id: 'an11',
    studentId: 's17',
    submittedBy: { name: 'Zane Vētra', relationship: 'Mother' },
    dateFrom: '2026-03-17',
    dateTo: '2026-03-18',
    periods: [1, 2, 3, 4, 5, 6, 7],
    isFullDay: true,
    parentNote:
      'Katrīna had flu symptoms — fever and sore throat. Doctor confirmed she should stay home for 2 days.',
    submittedAt: '2026-03-17T08:30:00',
    isLateSubmission: false,
    linkedAttendanceCount: 14,
    matchQuality: 'full',
    status: 'approved',
    processedAt: '2026-03-18T08:15:00',
    processedBy: 'Class Teacher',
    flags: [],
    internalComments: [],
    auditTrail: [
      { action: 'Note submitted', by: 'Zane Vētra', at: '2026-03-17T08:30:00' },
      { action: 'Approved', by: 'Class Teacher', at: '2026-03-18T08:15:00' },
    ],
  },
  {
    id: 'an12',
    studentId: 's2',
    submittedBy: { name: 'Aivars Ozoliņš', relationship: 'Father' },
    dateFrom: '2026-03-21',
    dateTo: '2026-03-21',
    periods: [1, 2, 3, 4, 5, 6, 7],
    isFullDay: true,
    parentNote: 'Sick.',
    submittedAt: '2026-03-24T07:00:00',
    isLateSubmission: true,
    linkedAttendanceCount: 7,
    matchQuality: 'full',
    status: 'needs_clarification',
    processedAt: '2026-03-24T10:00:00',
    processedBy: 'Class Teacher',
    flags: ['late_submission', 'frequent_absences', 'repeated_excuses'],
    internalComments: [
      {
        id: 'ic5',
        author: 'Class Teacher',
        text: 'Same one-word reason again. Need to talk to parent about attendance pattern.',
        createdAt: '2026-03-24T10:00:00',
      },
    ],
    auditTrail: [
      { action: 'Note submitted', by: 'Aivars Ozoliņš', at: '2026-03-24T07:00:00' },
      { action: 'Clarification requested', by: 'Class Teacher', at: '2026-03-24T10:00:00' },
    ],
  },
  {
    id: 'an13',
    studentId: 's26',
    submittedBy: { name: 'Uldis Lācis', relationship: 'Father' },
    dateFrom: '2026-03-28',
    dateTo: '2026-03-28',
    periods: [4, 5, 6, 7],
    isFullDay: false,
    parentNote: 'Krists has an orthodontist appointment at 12:00. Will leave after 3rd period.',
    submittedAt: '2026-03-27T20:00:00',
    isLateSubmission: false,
    linkedAttendanceCount: 4,
    matchQuality: 'full',
    status: 'pending',
    flags: [],
    internalComments: [],
    auditTrail: [{ action: 'Note submitted', by: 'Uldis Lācis', at: '2026-03-27T20:00:00' }],
  },
  {
    id: 'an14',
    studentId: 's3',
    submittedBy: { name: 'Dace Kalniņa', relationship: 'Mother' },
    dateFrom: '2026-03-28',
    dateTo: '2026-03-28',
    periods: [1],
    isFullDay: false,
    parentNote: 'Bus was late due to road works. Arrived 12 minutes after the bell.',
    submittedAt: '2026-03-28T09:00:00',
    isLateSubmission: false,
    linkedAttendanceCount: 1,
    matchQuality: 'none',
    status: 'pending',
    flags: [],
    internalComments: [],
    auditTrail: [{ action: 'Note submitted', by: 'Dace Kalniņa', at: '2026-03-28T09:00:00' }],
  },
]

export const MISSING_ABSENCE_NOTES: MissingAbsenceNote[] = [
  {
    id: 'mn1',
    studentId: 's2',
    absenceDates: ['2026-03-27'],
    totalPeriods: 7,
    daysSinceAbsence: 3,
    reminderSent: true,
    reminderSentAt: '2026-03-28T08:00:00',
  },
  {
    id: 'mn2',
    studentId: 's9',
    absenceDates: ['2026-03-27'],
    totalPeriods: 2,
    daysSinceAbsence: 3,
    reminderSent: false,
  },
  {
    id: 'mn3',
    studentId: 's8',
    absenceDates: ['2026-03-26', '2026-03-27'],
    totalPeriods: 10,
    daysSinceAbsence: 3,
    reminderSent: false,
  },
  {
    id: 'mn4',
    studentId: 's12',
    absenceDates: ['2026-03-25'],
    totalPeriods: 4,
    daysSinceAbsence: 5,
    reminderSent: true,
    reminderSentAt: '2026-03-27T08:00:00',
  },
  {
    id: 'mn5',
    studentId: 's4',
    absenceDates: ['2026-03-24'],
    totalPeriods: 3,
    daysSinceAbsence: 6,
    reminderSent: true,
    reminderSentAt: '2026-03-26T08:00:00',
  },
]

export function getPendingAbsenceNotes(): AbsenceNote[] {
  return ABSENCE_NOTES.filter((n) => n.status === 'pending')
}

export function computeAbsencePatterns(studentId: string): AbsenceNoteFlag[] {
  const studentNotes = ABSENCE_NOTES.filter((n) => n.studentId === studentId)
  const flags: AbsenceNoteFlag[] = []

  if (studentNotes.filter((n) => n.isLateSubmission).length >= 3) {
    flags.push('late_submission')
  }

  const totalAbsenceDays = studentNotes.reduce((sum, n) => {
    const from = new Date(n.dateFrom)
    const to = new Date(n.dateTo)
    return sum + Math.max(1, Math.round((to.getTime() - from.getTime()) / 86400000) + 1)
  }, 0)
  if (totalAbsenceDays >= 5) {
    flags.push('frequent_absences')
  }

  const dayOfWeekCounts = studentNotes.reduce(
    (acc, n) => {
      const day = new Date(n.dateFrom).getDay()
      if (day === 1 || day === 5) acc.monFri++
      acc.total++
      return acc
    },
    { monFri: 0, total: 0 }
  )
  if (dayOfWeekCounts.total >= 3 && dayOfWeekCounts.monFri / dayOfWeekCounts.total > 0.4) {
    flags.push('monday_friday')
  }

  const vagueReasons = ['sick', 'family reasons', 'family event', 'not feeling well']
  const vagueCount = studentNotes.filter((n) =>
    vagueReasons.some((r) => n.parentNote.toLowerCase().includes(r))
  ).length
  if (vagueCount >= 3) {
    flags.push('repeated_excuses')
  }

  return flags
}

export interface Subject {
  id: string
  name: string
  short: string
}

export const SUBJECTS: Subject[] = [
  { id: 'math', name: 'Mathematics', short: 'Math' },
  { id: 'latvian', name: 'Latvian Language', short: 'Latvian' },
  { id: 'english', name: 'English', short: 'English' },
  { id: 'literature', name: 'Literature', short: 'Lit.' },
  { id: 'history', name: 'History', short: 'History' },
  { id: 'physics', name: 'Physics', short: 'Physics' },
  { id: 'biology', name: 'Biology', short: 'Biology' },
  { id: 'chemistry', name: 'Chemistry', short: 'Chem.' },
]

export interface StudentGradeRow {
  studentId: string
  /** One grade per SUBJECTS entry. null = not yet graded. */
  grades: (number | null)[]
}

/** Grade overrides for students that already appear in GRADE_ALERTS */
const GRADE_ROSTER_OVERRIDES: Record<string, Record<string, number>> = {
  s4: { Mathematics: 4 },
  s8: { 'Latvian Language': 3 },
  s17: { Physics: 4 },
}

export function generateGradeRoster(): StudentGradeRow[] {
  return STUDENTS.map((student) => {
    const grades = SUBJECTS.map((subject) => {
      const override = GRADE_ROSTER_OVERRIDES[student.id]?.[subject.name]
      if (override !== undefined) return override

      const hash = simpleHash(`grade-${student.id}-${subject.id}`)
      const roll = hash % 22
      if (roll === 0) return null // not yet graded
      if (roll <= 2) return 4 // occasional low grade
      if (roll <= 5) return 5 + (hash % 2) // 5 or 6 — needs attention
      return 6 + (hash % 4) // 6–9 comfortable range
    })
    return { studentId: student.id, grades }
  })
}

export interface MissingSubmission {
  studentId: string
  subject: string
  count: number
}

export const MISSING_SUBMISSIONS: MissingSubmission[] = [
  { studentId: 's8', subject: 'Latvian Language', count: 3 },
  { studentId: 's4', subject: 'Mathematics', count: 2 },
  { studentId: 's12', subject: 'History', count: 1 },
]

export const GRADE_ALERTS: GradeAlert[] = [
  { studentId: 's4', subject: 'Mathematics', grade: '4', trend: 'down', previousGrade: '6' },
  { studentId: 's8', subject: 'Latvian Language', grade: '3', trend: 'down', previousGrade: '5' },
  { studentId: 's17', subject: 'Physics', grade: '4', trend: 'down', previousGrade: '7' },
]

export const COLLEAGUE_NOTES: ColleagueNote[] = [
  {
    id: 'cn1',
    authorName: 'M. Kalniņš',
    authorInitials: 'MK',
    studentId: 's4',
    body: 'Mārtiņš has been unable to concentrate in recent lessons, seems distracted and withdrawn. Possibly personal issues at home. I recommend discussing with the parents.',
    createdAt: '2026-03-30T09:15:00',
    isNew: true,
    category: 'wellbeing',
    status: 'needs_followup',
    priority: 'high',
  },
  {
    id: 'cn2',
    authorName: 'L. Ozola',
    authorInitials: 'LO',
    studentId: 's8',
    body: 'Emīls has not submitted the last 3 homework assignments in Latvian Language. When asked, says he forgot. No sign of difficulty understanding the material.',
    createdAt: '2026-03-29T14:20:00',
    isNew: true,
    category: 'academic',
    status: 'needs_followup',
    priority: 'normal',
  },
  {
    id: 'cn3',
    authorName: 'A. Pētersons',
    authorInitials: 'AP',
    studentId: 's2',
    body: 'Kārlis is regularly late for the first period. 3 times this week. Other students have mentioned he walks to school alone now.',
    createdAt: '2026-03-28T16:00:00',
    isNew: true,
    category: 'behavior',
    status: 'monitoring',
    priority: 'normal',
  },
  {
    id: 'cn4',
    authorName: 'I. Rūtenberga',
    authorInitials: 'IR',
    studentId: 's17',
    body: 'Katrīna showed strong initiative helping classmates during group work in Physics today. Much more engaged than last month after our intervention.',
    createdAt: '2026-03-28T11:45:00',
    isNew: true,
    category: 'positive',
    status: 'info',
    priority: 'normal',
  },
  {
    id: 'cn5',
    authorName: 'D. Liepa',
    authorInitials: 'DL',
    studentId: 's9',
    body: 'Alise had a conflict with two classmates during break. Raised voices, no physical contact. She was upset and refused to return to class for 10 minutes.',
    createdAt: '2026-03-27T13:30:00',
    isNew: false,
    category: 'social',
    status: 'needs_followup',
    priority: 'high',
  },
  {
    id: 'cn6',
    authorName: 'M. Kalniņš',
    authorInitials: 'MK',
    studentId: 's4',
    body: "Spoke with Mārtiņš's mother Sandra. She confirmed there have been changes at home. Agreed to check in weekly. Mother is cooperative.",
    createdAt: '2026-03-27T10:00:00',
    isNew: false,
    category: 'parent',
    status: 'monitoring',
    priority: 'normal',
  },
  {
    id: 'cn7',
    authorName: 'A. Pētersons',
    authorInitials: 'AP',
    studentId: 's12',
    body: 'Daniels disrupted the lesson twice today — talking loudly during explanation and throwing paper at another student. Warning given.',
    createdAt: '2026-03-26T15:10:00',
    isNew: false,
    category: 'behavior',
    status: 'info',
    priority: 'normal',
  },
  {
    id: 'cn8',
    authorName: 'L. Ozola',
    authorInitials: 'LO',
    studentId: 's1',
    body: 'Anna completed homework regularly this week after previous difficulties. Significant improvement in participation during class discussions.',
    createdAt: '2026-03-26T09:30:00',
    isNew: false,
    category: 'positive',
    status: 'info',
    priority: 'normal',
  },
  {
    id: 'cn9',
    authorName: 'D. Liepa',
    authorInitials: 'DL',
    studentId: 's9',
    body: 'Follow-up on the peer conflict: spoke with all three students separately. Alise apologized. The other students accepted. Monitoring for the next two weeks.',
    createdAt: '2026-03-26T08:45:00',
    isNew: false,
    category: 'followup',
    status: 'monitoring',
    priority: 'normal',
  },
  {
    id: 'cn10',
    authorName: 'I. Rūtenberga',
    authorInitials: 'IR',
    studentId: 's3',
    body: 'Elīna seems unusually tired and quiet this week. Avoided group work in two lessons and sat alone during breaks. Worth keeping an eye on.',
    createdAt: '2026-03-25T14:00:00',
    isNew: false,
    category: 'wellbeing',
    status: 'needs_followup',
    priority: 'normal',
  },
  {
    id: 'cn11',
    authorName: 'A. Pētersons',
    authorInitials: 'AP',
    studentId: 's2',
    body: "Contacted Kārlis's parents about repeated lateness. Father mentioned morning routine issues after schedule change. Agreed to try earlier departure.",
    createdAt: '2026-03-24T17:00:00',
    isNew: false,
    category: 'parent',
    status: 'resolved',
    priority: 'normal',
  },
  {
    id: 'cn12',
    authorName: 'M. Kalniņš',
    authorInitials: 'MK',
    studentId: 's14',
    body: 'Gustavs has been consistently well-prepared for lessons and actively participated in all group activities this month. Good progress.',
    createdAt: '2026-03-24T10:20:00',
    isNew: false,
    category: 'positive',
    status: 'info',
    priority: 'normal',
  },
  {
    id: 'cn13',
    authorName: 'L. Ozola',
    authorInitials: 'LO',
    studentId: 's8',
    body: 'Emīls was disengaged again today. Sat at the back, did not take notes, and did not participate when called upon. Third time this week.',
    createdAt: '2026-03-23T11:15:00',
    isNew: false,
    category: 'academic',
    status: 'monitoring',
    priority: 'normal',
  },
  {
    id: 'cn14',
    authorName: 'D. Liepa',
    authorInitials: 'DL',
    studentId: 's5',
    body: 'Ieva mentioned feeling anxious about upcoming exams. She asked if she could speak to someone. Referred to school support specialist.',
    createdAt: '2026-03-22T13:00:00',
    isNew: false,
    category: 'wellbeing',
    status: 'resolved',
    priority: 'normal',
  },
  {
    id: 'cn15',
    authorName: 'A. Pētersons',
    authorInitials: 'AP',
    studentId: 's12',
    body: "Daniels was involved in another disruption today. Pushed a classmate's chair during group work. Meeting with parents should be scheduled.",
    createdAt: '2026-03-21T14:30:00',
    isNew: false,
    category: 'behavior',
    status: 'needs_followup',
    priority: 'high',
  },
]

export const ABSENCE_THRESHOLDS = [
  {
    id: 'warning' as const,
    label: 'ekoolClassTeacher.tierWarning',
    threshold: 5,
    color: 'text-warning',
  },
  {
    id: 'concern' as const,
    label: 'ekoolClassTeacher.tierConcern',
    threshold: 10,
    color: 'text-orange-500',
  },
  {
    id: 'critical' as const,
    label: 'ekoolClassTeacher.tierCritical',
    threshold: 20,
    color: 'text-destructive',
  },
]

export const STUDENT_ATTENDANCE_STATS: StudentAttendanceStats[] = [
  {
    studentId: 's1',
    totalScheduledLessons: 210,
    unexcusedAbsences: 2,
    excusedAbsences: 3,
    latenessCount: 1,
    unexcusedPercent: 1.0,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
  {
    studentId: 's2',
    totalScheduledLessons: 210,
    unexcusedAbsences: 44,
    excusedAbsences: 7,
    latenessCount: 8,
    unexcusedPercent: 21.0,
    trend: 'worsening',
    frequentlyMissedPeriods: [1, 2],
    alertTier: 'critical',
  },
  {
    studentId: 's3',
    totalScheduledLessons: 210,
    unexcusedAbsences: 3,
    excusedAbsences: 2,
    latenessCount: 6,
    unexcusedPercent: 1.4,
    trend: 'stable',
    frequentlyMissedPeriods: [1],
    alertTier: 'none',
  },
  {
    studentId: 's4',
    totalScheduledLessons: 210,
    unexcusedAbsences: 25,
    excusedAbsences: 5,
    latenessCount: 2,
    unexcusedPercent: 11.9,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'concern',
  },
  {
    studentId: 's5',
    totalScheduledLessons: 210,
    unexcusedAbsences: 4,
    excusedAbsences: 6,
    latenessCount: 0,
    unexcusedPercent: 1.9,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
  {
    studentId: 's6',
    totalScheduledLessons: 210,
    unexcusedAbsences: 1,
    excusedAbsences: 0,
    latenessCount: 0,
    unexcusedPercent: 0.5,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
  {
    studentId: 's7',
    totalScheduledLessons: 210,
    unexcusedAbsences: 0,
    excusedAbsences: 0,
    latenessCount: 0,
    unexcusedPercent: 0,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
  {
    studentId: 's8',
    totalScheduledLessons: 210,
    unexcusedAbsences: 15,
    excusedAbsences: 3,
    latenessCount: 4,
    unexcusedPercent: 7.1,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'warning',
  },
  {
    studentId: 's9',
    totalScheduledLessons: 210,
    unexcusedAbsences: 31,
    excusedAbsences: 4,
    latenessCount: 1,
    unexcusedPercent: 14.8,
    trend: 'worsening',
    frequentlyMissedPeriods: [6, 7],
    alertTier: 'concern',
  },
  {
    studentId: 's10',
    totalScheduledLessons: 210,
    unexcusedAbsences: 2,
    excusedAbsences: 1,
    latenessCount: 0,
    unexcusedPercent: 1.0,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
  {
    studentId: 's11',
    totalScheduledLessons: 210,
    unexcusedAbsences: 0,
    excusedAbsences: 1,
    latenessCount: 0,
    unexcusedPercent: 0,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
  {
    studentId: 's12',
    totalScheduledLessons: 210,
    unexcusedAbsences: 13,
    excusedAbsences: 2,
    latenessCount: 2,
    unexcusedPercent: 6.2,
    trend: 'worsening',
    frequentlyMissedPeriods: null,
    alertTier: 'warning',
  },
  {
    studentId: 's13',
    totalScheduledLessons: 210,
    unexcusedAbsences: 3,
    excusedAbsences: 1,
    latenessCount: 1,
    unexcusedPercent: 1.4,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
  {
    studentId: 's14',
    totalScheduledLessons: 210,
    unexcusedAbsences: 17,
    excusedAbsences: 9,
    latenessCount: 1,
    unexcusedPercent: 8.1,
    trend: 'improving',
    frequentlyMissedPeriods: null,
    alertTier: 'warning',
  },
  {
    studentId: 's15',
    totalScheduledLessons: 210,
    unexcusedAbsences: 1,
    excusedAbsences: 2,
    latenessCount: 0,
    unexcusedPercent: 0.5,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
  {
    studentId: 's16',
    totalScheduledLessons: 210,
    unexcusedAbsences: 5,
    excusedAbsences: 1,
    latenessCount: 1,
    unexcusedPercent: 2.4,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
  {
    studentId: 's17',
    totalScheduledLessons: 210,
    unexcusedAbsences: 23,
    excusedAbsences: 3,
    latenessCount: 3,
    unexcusedPercent: 11.0,
    trend: 'improving',
    frequentlyMissedPeriods: null,
    alertTier: 'concern',
  },
  {
    studentId: 's18',
    totalScheduledLessons: 210,
    unexcusedAbsences: 2,
    excusedAbsences: 0,
    latenessCount: 0,
    unexcusedPercent: 1.0,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
  {
    studentId: 's19',
    totalScheduledLessons: 210,
    unexcusedAbsences: 0,
    excusedAbsences: 0,
    latenessCount: 0,
    unexcusedPercent: 0,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
  {
    studentId: 's20',
    totalScheduledLessons: 210,
    unexcusedAbsences: 11,
    excusedAbsences: 4,
    latenessCount: 0,
    unexcusedPercent: 5.2,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'warning',
  },
  {
    studentId: 's21',
    totalScheduledLessons: 210,
    unexcusedAbsences: 3,
    excusedAbsences: 2,
    latenessCount: 1,
    unexcusedPercent: 1.4,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
  {
    studentId: 's22',
    totalScheduledLessons: 210,
    unexcusedAbsences: 1,
    excusedAbsences: 0,
    latenessCount: 0,
    unexcusedPercent: 0.5,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
  {
    studentId: 's23',
    totalScheduledLessons: 210,
    unexcusedAbsences: 4,
    excusedAbsences: 3,
    latenessCount: 2,
    unexcusedPercent: 1.9,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
  {
    studentId: 's24',
    totalScheduledLessons: 210,
    unexcusedAbsences: 0,
    excusedAbsences: 0,
    latenessCount: 0,
    unexcusedPercent: 0,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
  {
    studentId: 's25',
    totalScheduledLessons: 210,
    unexcusedAbsences: 2,
    excusedAbsences: 1,
    latenessCount: 0,
    unexcusedPercent: 1.0,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
  {
    studentId: 's26',
    totalScheduledLessons: 210,
    unexcusedAbsences: 6,
    excusedAbsences: 2,
    latenessCount: 1,
    unexcusedPercent: 2.9,
    trend: 'stable',
    frequentlyMissedPeriods: null,
    alertTier: 'none',
  },
]

export type AcademicStatus = 'good' | 'watch' | 'atRisk' | 'critical'
export type OverallUrgency = 'good' | 'watch' | 'urgent'
export type SubjectStatus = 'good' | 'watch' | 'failing' | 'noData'
export type AcademicTrend = 'improving' | 'stable' | 'worsening'

export interface IndividualGrade {
  value: number
  type: 'test' | 'homework' | 'classwork' | 'quiz' | 'project'
  date: string
}

export interface SubjectAcademicInfo {
  subjectId: string
  status: SubjectStatus
  grade: number | null
  trend: AcademicTrend
  missingCount: number
  individualGrades: IndividualGrade[]
}

const GRADE_TYPES: IndividualGrade['type'][] = ['test', 'homework', 'classwork', 'quiz', 'project']

function generateIndividualGrades(
  studentId: string,
  subjectId: string,
  quarterGrade: number | null
): IndividualGrade[] {
  if (quarterGrade === null) return []
  const seed = simpleHash(`grades-${studentId}-${subjectId}`)
  const count = 4 + (seed % 6) // 4–9 individual grades per subject
  const grades: IndividualGrade[] = []
  for (let i = 0; i < count; i++) {
    const h = simpleHash(`grade-${studentId}-${subjectId}-${i}`)
    const spread = Math.max(1, Math.min(10, quarterGrade + (h % 5) - 2))
    const type = GRADE_TYPES[h % GRADE_TYPES.length]!
    const day = 1 + (h % 28)
    const month = 1 + (h % 3) // Jan–Mar for Q3
    grades.push({ value: spread, type, date: `2026-0${month}-${String(day).padStart(2, '0')}` })
  }
  grades.sort((a, b) => a.date.localeCompare(b.date))
  return grades
}

export interface StudentAcademicOverview {
  studentId: string
  overallStatus: AcademicStatus
  failingCount: number
  watchCount: number
  trend: AcademicTrend
  missingDataCount: number
  subjectStatuses: SubjectAcademicInfo[]
}

const GRADE_ROSTER_CACHE = generateGradeRoster()

export function computeStudentAcademicOverview(studentId: string): StudentAcademicOverview {
  const row = GRADE_ROSTER_CACHE.find((r) => r.studentId === studentId)
  const alerts = GRADE_ALERTS.filter((a) => a.studentId === studentId)
  const missing = MISSING_SUBMISSIONS.filter((m) => m.studentId === studentId)

  const subjectStatuses: SubjectAcademicInfo[] = SUBJECTS.map((subject, idx) => {
    const grade = row?.grades[idx] ?? null
    const alert = alerts.find((a) => a.subject === subject.name)
    const miss = missing.find((m) => m.subject === subject.name)

    let status: SubjectStatus = 'good'
    if (grade === null) status = 'noData'
    else if (grade <= 4) status = 'failing'
    else if (grade <= 6) status = 'watch'

    let trend: AcademicTrend = 'stable'
    if (alert) trend = alert.trend === 'down' ? 'worsening' : 'improving'

    return {
      subjectId: subject.id,
      status,
      grade,
      trend,
      missingCount: miss?.count ?? 0,
      individualGrades: generateIndividualGrades(studentId, subject.id, grade),
    }
  })

  const failingCount = subjectStatuses.filter((s) => s.status === 'failing').length
  const watchCount = subjectStatuses.filter((s) => s.status === 'watch').length
  const missingDataCount = subjectStatuses.filter((s) => s.status === 'noData').length
  const hasWorsening = subjectStatuses.some((s) => s.trend === 'worsening')
  const hasImproving = subjectStatuses.some((s) => s.trend === 'improving')

  let overallStatus: AcademicStatus = 'good'
  if (failingCount >= 3 || (failingCount >= 2 && hasWorsening)) overallStatus = 'critical'
  else if (failingCount >= 1 || missingDataCount >= 2) overallStatus = 'atRisk'
  else if (watchCount >= 2 || missingDataCount >= 1) overallStatus = 'watch'

  let trend: AcademicTrend = 'stable'
  if (hasWorsening && !hasImproving) trend = 'worsening'
  else if (hasImproving && !hasWorsening) trend = 'improving'

  return {
    studentId,
    overallStatus,
    failingCount,
    watchCount,
    trend,
    missingDataCount,
    subjectStatuses,
  }
}

export function computeAllStudentOverviews(): StudentAcademicOverview[] {
  return STUDENTS.map((s) => computeStudentAcademicOverview(s.id))
}

export interface ParentContact {
  name: string
  phone: string
  email: string
}

export interface EnrichedStudent {
  student: Student
  attendance: {
    totalAbsences: number
    unjustifiedAbsences: number
    lateArrivals: number
    trend: 'improving' | 'stable' | 'worsening'
    alertTier: AlertTier
  }
  academic: {
    status: AcademicStatus
    failingSubjects: number
    missingWork: number
    trendDirection: 'up' | 'down' | 'stable'
  }
  notes: {
    recentCount: number
    hasSeriousRecent: boolean
    latestType: string | null
  }
  absenceNotes: {
    pendingCount: number
  }
  parentContact: {
    parent: ParentContact
    lastContactDate: string | null
    daysSinceContact: number | null
  }
  urgency: OverallUrgency
  supportFlag: boolean
}

const PARENT_CONTACTS: Record<string, ParentContact> = {
  s1: { name: 'Inga Bērziņa', phone: '+371 2911 1001', email: 'inga.berzina@inbox.lv' },
  s2: { name: 'Aivars Ozoliņš', phone: '+371 2911 1002', email: 'aivars.ozolins@inbox.lv' },
  s3: { name: 'Dace Kalniņa', phone: '+371 2911 1003', email: 'dace.kalnina@inbox.lv' },
  s4: { name: 'Sandra Jansone', phone: '+371 2911 1004', email: 'sandra.jansone@inbox.lv' },
  s5: { name: 'Māra Liepiņa', phone: '+371 2911 1005', email: 'mara.liepina@inbox.lv' },
  s6: { name: 'Jānis Vanags', phone: '+371 2911 1006', email: 'janis.vanags@inbox.lv' },
  s7: { name: 'Ilze Zariņa', phone: '+371 2911 1007', email: 'ilze.zarina@inbox.lv' },
  s8: { name: 'Andris Krūmiņš', phone: '+371 2911 1008', email: 'andris.krumins@inbox.lv' },
  s9: { name: 'Līga Vītoliņa', phone: '+371 2911 1009', email: 'liga.vitolina@inbox.lv' },
  s10: { name: 'Pēteris Feldmanis', phone: '+371 2911 1010', email: 'peteris.feldmanis@inbox.lv' },
  s11: { name: 'Baiba Priede', phone: '+371 2911 1011', email: 'baiba.priede@inbox.lv' },
  s12: { name: 'Māris Celmiņš', phone: '+371 2911 1012', email: 'maris.celmins@inbox.lv' },
  s13: { name: 'Inese Siliņa', phone: '+371 2911 1013', email: 'inese.silina@inbox.lv' },
  s14: { name: 'Guntis Eglītis', phone: '+371 2911 1014', email: 'guntis.eglitis@inbox.lv' },
  s15: { name: 'Anita Rudzīte', phone: '+371 2911 1015', email: 'anita.rudzite@inbox.lv' },
  s16: { name: 'Valdis Balodis', phone: '+371 2911 1016', email: 'valdis.balodis@inbox.lv' },
  s17: { name: 'Zane Vētra', phone: '+371 2911 1017', email: 'zane.vetra@inbox.lv' },
  s18: { name: 'Atis Rieksts', phone: '+371 2911 1018', email: 'atis.rieksts@inbox.lv' },
  s19: { name: 'Evija Sproģe', phone: '+371 2911 1019', email: 'evija.sproge@inbox.lv' },
  s20: { name: 'Aigars Dzenis', phone: '+371 2911 1020', email: 'aigars.dzenis@inbox.lv' },
  s21: { name: 'Vita Kaža', phone: '+371 2911 1021', email: 'vita.kaza@inbox.lv' },
  s22: { name: 'Ivars Vilciņš', phone: '+371 2911 1022', email: 'ivars.vilcins@inbox.lv' },
  s23: { name: 'Rasma Bite', phone: '+371 2911 1023', email: 'rasma.bite@inbox.lv' },
  s24: { name: 'Gunārs Gailis', phone: '+371 2911 1024', email: 'gunars.gailis@inbox.lv' },
  s25: { name: 'Maija Skuja', phone: '+371 2911 1025', email: 'maija.skuja@inbox.lv' },
  s26: { name: 'Uldis Lācis', phone: '+371 2911 1026', email: 'uldis.lacis@inbox.lv' },
}

const LAST_PARENT_CONTACT: Record<string, string | null> = {
  s1: '2026-03-25',
  s2: null,
  s3: '2026-03-20',
  s4: null,
  s5: '2026-03-22',
  s6: '2026-03-18',
  s7: '2026-03-26',
  s8: '2026-02-28',
  s9: '2026-03-10',
  s10: '2026-03-24',
  s11: '2026-03-19',
  s12: '2026-03-05',
  s13: '2026-03-27',
  s14: '2026-03-15',
  s15: '2026-03-21',
  s16: '2026-03-23',
  s17: '2026-02-25',
  s18: '2026-03-26',
  s19: '2026-03-28',
  s20: '2026-03-08',
  s21: '2026-03-17',
  s22: '2026-03-25',
  s23: '2026-03-22',
  s24: '2026-03-27',
  s25: '2026-03-20',
  s26: '2026-03-12',
}

function daysBetween(from: string, to: string): number {
  return Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86400000)
}

function computeAcademicStatus(studentId: string): {
  status: AcademicStatus
  failingSubjects: number
  missingWork: number
  trendDirection: 'up' | 'down' | 'stable'
} {
  const overview = computeStudentAcademicOverview(studentId)
  const missing = MISSING_SUBMISSIONS.filter((m) => m.studentId === studentId)
  const missingWork = missing.reduce((sum, m) => sum + m.count, 0)
  const trendDirection =
    overview.trend === 'worsening'
      ? ('down' as const)
      : overview.trend === 'improving'
        ? ('up' as const)
        : ('stable' as const)
  return {
    status: overview.overallStatus,
    failingSubjects: overview.failingCount,
    missingWork,
    trendDirection,
  }
}

function computeUrgency(
  attendanceTier: AlertTier,
  academic: AcademicStatus,
  hasSeriousNote: boolean,
  pendingAbsenceNotes: number,
  daysSinceParentContact: number | null
): OverallUrgency {
  const issues: number[] = []
  if (attendanceTier === 'critical' || attendanceTier === 'concern') issues.push(2)
  else if (attendanceTier === 'warning') issues.push(1)
  if (academic === 'critical') issues.push(3)
  else if (academic === 'atRisk') issues.push(2)
  else if (academic === 'watch') issues.push(1)
  if (hasSeriousNote) issues.push(1)
  if (pendingAbsenceNotes > 0) issues.push(1)
  if (daysSinceParentContact !== null && daysSinceParentContact > 21) issues.push(1)

  const total = issues.reduce((s, v) => s + v, 0)
  if (total >= 3) return 'urgent'
  if (total >= 1) return 'watch'
  return 'good'
}

const TODAY = '2026-03-30'

export function buildEnrichedStudents(): EnrichedStudent[] {
  return STUDENTS.map((student) => {
    const stats = getStudentStats(student.id)
    const academic = computeAcademicStatus(student.id)
    const studentNotes = COLLEAGUE_NOTES.filter((n) => n.studentId === student.id)
    const pendingNotes = ABSENCE_NOTES.filter(
      (n) => n.studentId === student.id && n.status === 'pending'
    )
    const lastContact = LAST_PARENT_CONTACT[student.id] ?? null
    const daysSince = lastContact ? daysBetween(lastContact, TODAY) : null
    const hasSeriousRecent = studentNotes.some((n) => n.isNew)

    const urgency = computeUrgency(
      stats?.alertTier ?? 'none',
      academic.status,
      hasSeriousRecent,
      pendingNotes.length,
      daysSince
    )

    return {
      student,
      attendance: {
        totalAbsences: stats ? stats.unexcusedAbsences + stats.excusedAbsences : 0,
        unjustifiedAbsences: stats?.unexcusedAbsences ?? 0,
        lateArrivals: stats?.latenessCount ?? 0,
        trend: stats?.trend ?? 'stable',
        alertTier: stats?.alertTier ?? 'none',
      },
      academic,
      notes: {
        recentCount: studentNotes.length,
        hasSeriousRecent,
        latestType: studentNotes.length > 0 ? 'behavior' : null,
      },
      absenceNotes: {
        pendingCount: pendingNotes.length,
      },
      parentContact: {
        parent: PARENT_CONTACTS[student.id] ?? { name: 'Unknown', phone: '', email: '' },
        lastContactDate: lastContact,
        daysSinceContact: daysSince,
      },
      urgency,
      supportFlag:
        urgency === 'urgent' && (stats?.alertTier === 'critical' || academic.status === 'atRisk'),
    }
  })
}

export const ENRICHED_STUDENTS = buildEnrichedStudents()

export function getStudentById(id: string): Student | undefined {
  return STUDENTS.find((s) => s.id === id)
}

export function getStudentStats(studentId: string): StudentAttendanceStats | undefined {
  return STUDENT_ATTENDANCE_STATS.find((s) => s.studentId === studentId)
}

export function generateAttendanceGrid(): Map<string, AttendanceDay> {
  const grid = new Map<string, AttendanceDay>()
  for (const s of STUDENTS) {
    const absence = TODAY_ABSENCES.find((a) => a.studentId === s.id)
    if (absence) {
      grid.set(s.id, {
        studentId: s.id,
        status: 'absent',
        periods: absence.periods,
        excused: absence.excused,
        note: absence.reason ?? undefined,
      })
    } else if (s.id === 's3') {
      grid.set(s.id, {
        studentId: s.id,
        status: 'late',
        periods: [1],
        minutesLate: 12,
      })
    } else if (s.id === 's7') {
      grid.set(s.id, {
        studentId: s.id,
        status: 'exempt',
        periods: [5],
        excused: true,
        exemptSubject: 'PE',
      })
    } else {
      grid.set(s.id, {
        studentId: s.id,
        status: 'present',
        periods: [],
      })
    }
  }
  return grid
}

function simpleHash(str: string): number {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0
  }
  return Math.abs(hash)
}

export function getMonday(weekOffset: number): Date {
  const today = new Date()
  const day = today.getDay()
  const diff = day === 0 ? -6 : 1 - day
  const monday = new Date(today)
  monday.setDate(today.getDate() + diff + weekOffset * 7)
  monday.setHours(0, 0, 0, 0)
  return monday
}

export function generateWeekAttendance(weekOffset: number): StudentWeekAttendance[] {
  const monday = getMonday(weekOffset)
  const today = new Date()
  today.setHours(23, 59, 59, 999)

  return STUDENTS.map((student) => {
    const stats = getStudentStats(student.id)
    const absenceRate = stats ? stats.unexcusedPercent : 0

    const days: WeekDaySummary[] = []
    let weekAbsentDays = 0
    let weekLateDays = 0

    for (let d = 0; d < 5; d++) {
      const date = new Date(monday)
      date.setDate(monday.getDate() + d)
      const dateStr = toISODateString(date)

      if (date > today) {
        days.push({
          date: dateStr,
          status: 'future',
          absentPeriods: 0,
          totalPeriods: TOTAL_PERIODS,
          excused: false,
        })
        continue
      }

      const hash = simpleHash(`${student.id}-${dateStr}`)
      const roll = hash % 100

      if (roll < absenceRate * 2.5) {
        const absentPeriods = (hash % 5) + 3
        const excused = hash % 4 === 0
        days.push({
          date: dateStr,
          status: 'absent',
          absentPeriods: Math.min(absentPeriods, TOTAL_PERIODS),
          totalPeriods: TOTAL_PERIODS,
          excused,
        })
        weekAbsentDays++
      } else if (roll < absenceRate * 3.5 && (stats?.latenessCount ?? 0) > 3) {
        const minutesLate = (hash % 15) + 5
        days.push({
          date: dateStr,
          status: 'late',
          absentPeriods: 0,
          totalPeriods: TOTAL_PERIODS,
          excused: false,
          minutesLate,
        })
        weekLateDays++
      } else {
        days.push({
          date: dateStr,
          status: 'present',
          absentPeriods: 0,
          totalPeriods: TOTAL_PERIODS,
          excused: false,
        })
      }
    }

    return { studentId: student.id, days, weekAbsentDays, weekLateDays }
  })
}

// ---------------------------------------------------------------------------
// Assignments
// ---------------------------------------------------------------------------

export type AssignmentType = 'homework' | 'test' | 'quiz' | 'essay' | 'project' | 'presentation'

export interface Assignment {
  id: string
  title: string
  subject: string
  type: AssignmentType
  assignedDate: string
  dueDate: string
  assignedBy: string
  affectsWholeClass: boolean
  affectedStudentIds: string[]
  missingSubmissions: number
}

export const ASSIGNMENTS: Assignment[] = [
  {
    id: 'a1',
    title: 'Quadratic Equations — Chapter 5',
    subject: 'Mathematics',
    type: 'test',
    assignedDate: '2026-03-27',
    dueDate: '2026-04-01',
    assignedBy: 'Inga Liepiņa',
    affectsWholeClass: true,
    affectedStudentIds: [],
    missingSubmissions: 0,
  },
  {
    id: 'a2',
    title: 'Past Simple vs Present Perfect',
    subject: 'English',
    type: 'homework',
    assignedDate: '2026-04-01',
    dueDate: '2026-04-03',
    assignedBy: 'Māra Kalniņa',
    affectsWholeClass: true,
    affectedStudentIds: [],
    missingSubmissions: 0,
  },
  {
    id: 'a3',
    title: 'Cell Division Diagram',
    subject: 'Biology',
    type: 'homework',
    assignedDate: '2026-04-01',
    dueDate: '2026-04-04',
    assignedBy: 'Aija Vītola',
    affectsWholeClass: true,
    affectedStudentIds: [],
    missingSubmissions: 0,
  },
  {
    id: 'a4',
    title: 'Essay: Latvian Independence Movement',
    subject: 'History',
    type: 'essay',
    assignedDate: '2026-03-28',
    dueDate: '2026-04-07',
    assignedBy: 'Jānis Ozoliņš',
    affectsWholeClass: true,
    affectedStudentIds: [],
    missingSubmissions: 2,
  },
  {
    id: 'a5',
    title: 'Latvian Grammar — Verb Conjugation',
    subject: 'Latvian Language',
    type: 'homework',
    assignedDate: '2026-04-01',
    dueDate: '2026-04-02',
    assignedBy: 'Dace Bērziņa',
    affectsWholeClass: true,
    affectedStudentIds: [],
    missingSubmissions: 0,
  },
  {
    id: 'a6',
    title: 'Periodic Table Quiz',
    subject: 'Chemistry',
    type: 'quiz',
    assignedDate: '2026-03-31',
    dueDate: '2026-04-01',
    assignedBy: 'Raimonds Grundmanis',
    affectsWholeClass: true,
    affectedStudentIds: [],
    missingSubmissions: 3,
  },
  {
    id: 'a7',
    title: 'Book Report: Rainis Poetry',
    subject: 'Literature',
    type: 'project',
    assignedDate: '2026-03-24',
    dueDate: '2026-04-08',
    assignedBy: 'Dace Bērziņa',
    affectsWholeClass: true,
    affectedStudentIds: [],
    missingSubmissions: 0,
  },
  {
    id: 'a8',
    title: 'Physics Lab Report — Optics',
    subject: 'Physics',
    type: 'homework',
    assignedDate: '2026-03-31',
    dueDate: '2026-04-03',
    assignedBy: 'Andris Kalējs',
    affectsWholeClass: true,
    affectedStudentIds: [],
    missingSubmissions: 0,
  },
  {
    id: 'a9',
    title: 'English Presentation: My Favourite Book',
    subject: 'English',
    type: 'presentation',
    assignedDate: '2026-04-01',
    dueDate: '2026-04-10',
    assignedBy: 'Māra Kalniņa',
    affectsWholeClass: false,
    affectedStudentIds: ['s1', 's5', 's9', 's13', 's17', 's21', 's25'],
    missingSubmissions: 0,
  },
  {
    id: 'a10',
    title: 'Mathematics Homework — Functions',
    subject: 'Mathematics',
    type: 'homework',
    assignedDate: '2026-03-31',
    dueDate: '2026-04-01',
    assignedBy: 'Inga Liepiņa',
    affectsWholeClass: true,
    affectedStudentIds: [],
    missingSubmissions: 4,
  },
]

export function getAssignmentsDueToday(): Assignment[] {
  const todayStr = toISODateString(new Date())
  return ASSIGNMENTS.filter((a) => a.dueDate === todayStr)
}

export function getAssignmentsCreatedToday(): Assignment[] {
  const todayStr = toISODateString(new Date())
  return ASSIGNMENTS.filter((a) => a.assignedDate === todayStr)
}

export function getAssignmentsDueThisWeek(): Assignment[] {
  const now = new Date()
  const todayStr = toISODateString(now)
  const weekEnd = new Date(now)
  weekEnd.setDate(now.getDate() + 7)
  const weekEndStr = toISODateString(weekEnd)
  return ASSIGNMENTS.filter((a) => a.dueDate >= todayStr && a.dueDate <= weekEndStr)
}

export function getAssignmentsWithMissing(): Assignment[] {
  return ASSIGNMENTS.filter((a) => a.missingSubmissions > 0)
}

// ---------------------------------------------------------------------------
// Calendar events
// ---------------------------------------------------------------------------

export type CalendarEventCategory =
  | 'school'
  | 'class'
  | 'parent'
  | 'deadline'
  | 'studentAway'
  | 'actionNeeded'
  | 'academic'

export type CalendarEventStatus =
  | 'upcoming'
  | 'needsPreparation'
  | 'awaitingResponse'
  | 'approved'
  | 'completed'

export interface CalendarEvent {
  id: string
  title: string
  date: string
  startTime?: string
  endTime?: string
  isAllDay: boolean
  category: CalendarEventCategory
  status: CalendarEventStatus
  description: string
  location?: string
  organizer?: string
  affectsWholeClass: boolean
  affectedStudentIds: string[]
  contextBadges: string[]
  linkedModule?: 'attendance' | 'absenceNotes' | 'notes' | 'grades'
}

export const CALENDAR_EVENTS: CalendarEvent[] = [
  {
    id: 'ce1',
    title: 'Parent Meeting — Class 9.a',
    date: '2026-03-31',
    startTime: '18:00',
    endTime: '19:30',
    isAllDay: false,
    category: 'parent',
    status: 'needsPreparation',
    description:
      'Semester review meeting with parents. Discuss attendance concerns, academic progress, and upcoming events.',
    location: 'Room 204',
    organizer: 'Class Teacher',
    affectsWholeClass: true,
    affectedStudentIds: [],
    contextBadges: ['Agenda not sent yet', '4 parents have not confirmed'],
    linkedModule: 'notes',
  },
  {
    id: 'ce2',
    title: 'Class Trip to Science Centre',
    date: '2026-04-03',
    startTime: '09:00',
    endTime: '15:00',
    isAllDay: false,
    category: 'class',
    status: 'needsPreparation',
    description:
      'Day trip to the National Science Centre. Buses depart at 08:45 from the school entrance.',
    location: 'National Science Centre',
    organizer: 'Class Teacher',
    affectsWholeClass: true,
    affectedStudentIds: [],
    contextBadges: ['3 consent forms missing', '2 payments pending'],
  },
  {
    id: 'ce3',
    title: 'Future Absence — Marta Rudzīte',
    date: '2026-04-02',
    isAllDay: true,
    category: 'studentAway',
    status: 'approved',
    description: 'Pre-approved absence for a medical procedure. Parent notified in advance.',
    affectsWholeClass: false,
    affectedStudentIds: ['s15'],
    contextBadges: [],
    linkedModule: 'absenceNotes',
  },
  {
    id: 'ce4',
    title: 'Trip Payment Deadline',
    date: '2026-04-08',
    isAllDay: true,
    category: 'deadline',
    status: 'awaitingResponse',
    description:
      'Final date to submit payment for the Science Centre trip. Students without payment cannot participate.',
    organizer: 'Administration',
    affectsWholeClass: false,
    affectedStudentIds: ['s4', 's8', 's12', 's20'],
    contextBadges: ['4 students unresolved'],
  },
  {
    id: 'ce5',
    title: 'Mathematics Test',
    date: '2026-04-07',
    startTime: '08:00',
    endTime: '08:40',
    isAllDay: false,
    category: 'academic',
    status: 'upcoming',
    description: 'End-of-chapter test covering quadratic equations and functions.',
    location: 'Room 204',
    organizer: 'M. Kalniņš',
    affectsWholeClass: true,
    affectedStudentIds: [],
    contextBadges: [],
    linkedModule: 'grades',
  },
  {
    id: 'ce6',
    title: 'School Photo Day',
    date: '2026-04-09',
    startTime: '10:00',
    endTime: '12:00',
    isAllDay: false,
    category: 'school',
    status: 'upcoming',
    description: 'Individual and class photos. Class 9.a is scheduled for 10:30.',
    location: 'Gymnasium',
    organizer: 'Administration',
    affectsWholeClass: true,
    affectedStudentIds: [],
    contextBadges: [],
  },
  {
    id: 'ce7',
    title: 'Competition — Kārlis Ozoliņš',
    date: '2026-04-06',
    startTime: '09:00',
    endTime: '16:00',
    isAllDay: false,
    category: 'studentAway',
    status: 'approved',
    description: 'Regional mathematics olympiad. Kārlis will be absent for the full day.',
    location: 'University of Latvia',
    affectsWholeClass: false,
    affectedStudentIds: ['s2'],
    contextBadges: [],
    linkedModule: 'attendance',
  },
  {
    id: 'ce8',
    title: 'Report Card Distribution',
    date: '2026-04-17',
    isAllDay: true,
    category: 'deadline',
    status: 'upcoming',
    description:
      'Q3 report cards to be handed out. Class teacher must finalize all comments by April 15.',
    organizer: 'Administration',
    affectsWholeClass: true,
    affectedStudentIds: [],
    contextBadges: ['Comments not finalized'],
    linkedModule: 'grades',
  },
  {
    id: 'ce9',
    title: 'Class Teacher Lesson',
    date: '2026-04-01',
    startTime: '13:00',
    endTime: '13:40',
    isAllDay: false,
    category: 'class',
    status: 'upcoming',
    description:
      'Weekly class teacher lesson. Topic: preparing for end-of-year exams and study techniques.',
    location: 'Room 204',
    organizer: 'Class Teacher',
    affectsWholeClass: true,
    affectedStudentIds: [],
    contextBadges: [],
  },
  {
    id: 'ce10',
    title: 'Parent Consultation Day',
    date: '2026-04-15',
    startTime: '16:00',
    endTime: '19:00',
    isAllDay: false,
    category: 'parent',
    status: 'upcoming',
    description:
      'Open consultation slots for individual parent meetings. Parents can book 15-minute slots.',
    location: 'Room 204',
    organizer: 'Class Teacher',
    affectsWholeClass: true,
    affectedStudentIds: [],
    contextBadges: ['Booking not opened yet'],
  },
  {
    id: 'ce11',
    title: 'Future Absence — Anna Bērziņa',
    date: '2026-04-10',
    isAllDay: true,
    category: 'studentAway',
    status: 'approved',
    description: 'Family travel. Parent submitted advance notice for 3 days (Apr 10–12).',
    affectsWholeClass: false,
    affectedStudentIds: ['s1'],
    contextBadges: ['3 days away'],
    linkedModule: 'absenceNotes',
  },
  {
    id: 'ce12',
    title: 'Consent Form Deadline — Science Trip',
    date: '2026-04-01',
    isAllDay: true,
    category: 'deadline',
    status: 'awaitingResponse',
    description: 'Last day to collect signed consent forms for the Science Centre trip.',
    organizer: 'Class Teacher',
    affectsWholeClass: false,
    affectedStudentIds: ['s8', 's12', 's20'],
    contextBadges: ['3 forms outstanding'],
  },
  {
    id: 'ce13',
    title: 'English Essay Submission',
    date: '2026-04-04',
    isAllDay: true,
    category: 'academic',
    status: 'upcoming',
    description: 'Deadline for the Q3 English essay. All students must submit via the e-journal.',
    organizer: 'L. Ozola',
    affectsWholeClass: true,
    affectedStudentIds: [],
    contextBadges: [],
    linkedModule: 'grades',
  },
  {
    id: 'ce14',
    title: 'Spring Concert Rehearsal',
    date: '2026-04-14',
    startTime: '14:00',
    endTime: '16:00',
    isAllDay: false,
    category: 'school',
    status: 'upcoming',
    description: 'Rehearsal for the school spring concert. Students performing should attend.',
    location: 'Assembly Hall',
    organizer: 'Music Teacher',
    affectsWholeClass: false,
    affectedStudentIds: ['s3', 's7', 's13', 's21', 's25'],
    contextBadges: ['5 students involved'],
  },
  {
    id: 'ce15',
    title: 'Mentoring Discussion — Emīls Krūmiņš',
    date: '2026-04-02',
    startTime: '14:00',
    endTime: '14:30',
    isAllDay: false,
    category: 'actionNeeded',
    status: 'needsPreparation',
    description:
      'Follow-up mentoring session with Emīls regarding academic engagement and homework submission pattern.',
    location: 'Counselling Room',
    organizer: 'Class Teacher',
    affectsWholeClass: false,
    affectedStudentIds: ['s8'],
    contextBadges: ['3 missing homework assignments'],
    linkedModule: 'notes',
  },
]

export function getCalendarSummary() {
  const now = new Date()
  const todayStr = toISODateString(now)
  const weekEnd = new Date(now)
  weekEnd.setDate(now.getDate() + 7)
  const weekEndStr = toISODateString(weekEnd)

  let today = 0
  let thisWeek = 0
  let needsPrep = 0
  let studentsAway = 0
  let awaitingResponse = 0

  for (const ev of CALENDAR_EVENTS) {
    if (ev.date === todayStr) today++
    if (ev.date >= todayStr && ev.date <= weekEndStr) thisWeek++
    if (ev.status === 'needsPreparation') needsPrep++
    if (ev.category === 'studentAway' && ev.date >= todayStr && ev.date <= weekEndStr)
      studentsAway++
    if (ev.status === 'awaitingResponse') awaitingResponse++
  }

  return { today, thisWeek, needsPrep, studentsAway, awaitingResponse }
}

export function getUpcomingCalendarEvents(limit = 4): CalendarEvent[] {
  const todayStr = toISODateString(new Date())
  return CALENDAR_EVENTS.filter((ev) => ev.date >= todayStr)
    .sort((a, b) => {
      const cmp = a.date.localeCompare(b.date)
      if (cmp !== 0) return cmp
      return (a.startTime ?? '').localeCompare(b.startTime ?? '')
    })
    .slice(0, limit)
}

export function getCalendarEventsForWeek(weekOffset: number): Map<string, CalendarEvent[]> {
  const monday = getMonday(weekOffset)
  const result = new Map<string, CalendarEvent[]>()

  for (let d = 0; d < 7; d++) {
    const date = new Date(monday)
    date.setDate(monday.getDate() + d)
    const dateStr = toISODateString(date)
    const dayEvents = CALENDAR_EVENTS.filter((ev) => ev.date === dateStr)
    if (dayEvents.length > 0) {
      result.set(dateStr, dayEvents)
    }
  }
  return result
}

export function getCalendarEventsForMonth(
  year: number,
  month: number
): Map<string, CalendarEvent[]> {
  const result = new Map<string, CalendarEvent[]>()
  const prefix = `${year}-${String(month + 1).padStart(2, '0')}`

  for (const ev of CALENDAR_EVENTS) {
    if (ev.date.startsWith(prefix)) {
      const existing = result.get(ev.date) ?? []
      existing.push(ev)
      result.set(ev.date, existing)
    }
  }
  return result
}

export function getCalendarOrganizers(): string[] {
  const set = new Set<string>()
  for (const ev of CALENDAR_EVENTS) {
    if (ev.organizer) set.add(ev.organizer)
  }
  return Array.from(set).sort()
}
